import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { routerActions } from 'connected-react-router';
import InlineDropdown from 'platform/common/components/InlineDropdown/InlineDropdown';
import UpdatedOn from 'platform/common/components/UpdatedOn/UpdatedOn';
import ListToolbar from 'platform/common/components/ListToolbar/ListToolbar';
import FormattedTable from 'platform/common/components/FormattedTable/FormattedTable';
import { DATA_TYPES } from 'platform/common/dataType';
import { TableCell } from 'platform/common/common.type';
import { RoleFormCreate, RoleFormEdit } from './RoleForm';
import * as userService from '../userManagement.service';
import { Role } from './role.type';

type State = {
    loading: boolean;
    data: Role[];
    searchQuery: string;
};

const RoleList = () => {
    const dispatch = useDispatch();

    const [state, setState] = useState<State>({
        loading: true,
        data: [],
        searchQuery: '',
    });

    const fetchRoles = () => {
        setState(currentState => ({ ...currentState, loading: true }));
        userService
            .fetchRoles()
            .then(response => {
                setState(currentState => ({ ...currentState, data: response }));
            })
            .finally(() => {
                setState(currentState => ({ ...currentState, loading: false }));
            });
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    const columnDefinitions = [
        {
            Header: 'ID',
            accessor: 'id',
            type: DATA_TYPES.ID,
            width: 80,
        },
        {
            Header: 'Name',
            accessor: 'title',
            minWidth: 200,
            type: DATA_TYPES.TEXT,
            Cell: (row: TableCell<Role>) => (
                <span>
                    {row.original.adminRole ? <i className="fa fa-lock fa-lg px-2" /> : null}
                    {row.original.title}
                </span>
            ),
        },
        {
            Header: 'Edited',
            accessor: 'updatedOn',
            width: 160,
            Cell: (row: TableCell<Role>) => (
                <UpdatedOn date={row.original.updatedOn} updatedBy={row.original.updatedBy} />
            ),
        },
        {
            className: () => 'edit-dropdown pull-right cell-align-right',
            width: 50,
            sortable: false,
            Cell: (row: TableCell<Role>) => (
                <InlineDropdown
                    items={[
                        {
                            label: 'Edit',
                            action: () =>
                                dispatch(routerActions.push(`/user-management/roles/${row.original.id}`)),
                        },
                    ]}
                />
            ),
        },
    ];

    const filterList = (list: Role[], searchQuery: string) => {
        if (!searchQuery) return list;
        const query = searchQuery.toLowerCase();
        return list.filter(
            r =>
                String(r.id)
                    .toLowerCase()
                    .includes(query) || r.title.toLowerCase().includes(query)
        );
    };

    return (
        <div>
            <div className="mb-4">
                <ListToolbar
                    value={state.searchQuery}
                    onCreate={() => dispatch(routerActions.push('/user-management/roles/new'))}
                    onSearch={searchQuery => setState(currentState => ({ ...currentState, searchQuery }))}
                />
            </div>
            <FormattedTable
                columns={columnDefinitions}
                data={filterList(state.data, state.searchQuery)}
                loading={state.loading}
            />
            <Switch>
                <Route
                    path="/user-management/roles/new"
                    render={props => (
                        <RoleFormCreate
                            {...props}
                            redirectTo="/user-management/roles"
                            canEdit
                            afterSubmit={(role: Role) =>
                                dispatch(routerActions.push(`/user-management/roles/${role.id}`))
                            }
                        />
                    )}
                />
                <Route
                    path="/user-management/roles/:id"
                    render={props => (
                        <RoleFormEdit
                            {...props}
                            redirectTo="/user-management/roles"
                            canEdit
                            onClose={() => fetchRoles()}
                        />
                    )}
                />
            </Switch>
        </div>
    );
};

export default RoleList;
